import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  convertFiltersFromLocation,
  convertFiltersToLocation,
} from '~/utils/common'
import { DEFAULT_PAGE_SIZE } from '~/constants/settings'
import { useUser } from '~/redux/hooks/user'
import { COMMENT_STATUS, COMMENT_STATUS_OPTIONS_2 } from '~/constants/comments'
import { useCommentList } from '~/redux/hooks/comment'
import { showDialog } from '~/components/common/Dialog'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import { CommentTable } from './CommentTable'

import useLastListState from '~/utils/hooks/useLastListState'
import { SECTION_LIST } from '~/constants/list'
import { useIsSyncedStore } from '~/redux/hooks/store'
import StoreSyncingWarning from '~/components/common/StoreSyncingWarning'
import SettingsLayout from '~/layouts/SettingsLayout'
import { Tab, TableController, Tabs } from 'bs-unified-ui'
import { Card, Divider, Theme, useTheme } from '@mui/material'

interface CommentsProps {}

export const Comments: React.FC<CommentsProps> = () => {
  const theme: Theme = useTheme()

  useLastListState(SECTION_LIST.COMMENTS, true)
  const isSyncedStore = useIsSyncedStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUser()
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string[]>([])

  const { actions, commentData, loading: loadingList, error } = useCommentList()
  const rowsPerPage = useMemo(
    () => Number(user.user_settings?.list_pagination) || DEFAULT_PAGE_SIZE,
    [user.user_settings?.list_pagination],
  )

  const filtersFromLocation = useMemo(() => {
    return convertFiltersFromLocation(location.search) as {
      page?: number
      search_parameter?: string
      _sortId?: string
      _direction?: string
      status?: string
    }
  }, [location.search])

  const {
    page,
    search_parameter,
    _sortId,
    _direction = '',
    status,
  } = filtersFromLocation

  const tab = useMemo(() => {
    if (COMMENT_STATUS_OPTIONS_2.some((s) => s.value === status)) return status

    return COMMENT_STATUS.PENDING
  }, [status])

  const fetchCommentList = useCallback(() => {
    if (!isSyncedStore) return
    if (!status) return

    actions.fetchCommentList({
      page: page || 1,
      limit: rowsPerPage,
      filters: {
        status: tab,
      },
      ...(_sortId ? { sort: { [_sortId]: Number(_direction) } } : {}),
      ...(search_parameter ? { search_parameter } : {}),
    })
  }, [
    isSyncedStore,
    rowsPerPage,
    status,
    page,
    _sortId,
    _direction,
    search_parameter,
    actions,
    tab,
  ])

  const updateFilter = useCallback(
    (newFilters = {}) => {
      const newLocationSearch = {
        ...filtersFromLocation,
        ...newFilters,
      }

      navigate({
        pathname: location.pathname,
        // @ts-ignore
        search: convertFiltersToLocation(newLocationSearch),
      })
    },
    [location.pathname, filtersFromLocation],
  )

  const handleChangeTab = useCallback(
    (t) => {
      updateFilter({
        status: t,
        page: null,
      })
    },
    [location],
  )

  useEffect(() => {
    fetchCommentList()
  }, [fetchCommentList])

  useEffect(() => {
    if (COMMENT_STATUS_OPTIONS_2.every((s) => s?.value !== status)) {
      handleChangeTab(COMMENT_STATUS.PENDING)
    }
  }, [status])

  useEffect(() => {
    if (error) {
      showDialog(dialogParams.error(error))
    }
  }, [error])

  const getCountByStatus = (s) => {
    if (s === COMMENT_STATUS.PENDING) return commentData.count_under_review || 0
    if (s === COMMENT_STATUS.APPROVED) return commentData.count_published || 0
    if (s === COMMENT_STATUS.SPAM) return commentData.count_spam || 0
    if (s === COMMENT_STATUS.TRASH) return commentData.count_trash || 0
    return 0
  }

  const sortingOptions = [
    {
      label: 'Rating',
      value: 'rating',
    },
    {
      label: 'Customer',
      value: 'reviewer',
    },
    {
      label: 'Time',
      value: 'date_created_gmt',
    },
  ]

  return (
    <SettingsLayout
      title="Comments"
      systemLayout
      sx={{
        minHeight: 'unset',
        height: '100%',
        overflowY: 'hidden',

        '>.MuiContainer-root': {
          height: '100%',
          '>.MuiStack-root': { height: '100%' },
        },
      }}
    >
      <Tabs value={tab} onChange={(_, t) => handleChangeTab(t)}>
        {COMMENT_STATUS_OPTIONS_2.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={`${t.label} (${getCountByStatus(t.value)})`}
          />
        ))}
      </Tabs>

      <Card
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mb: selected.length > 0 ? `${theme.spacing(9)} !important` : 0,
        }}
      >
        <TableController.Controller outlined={false}>
          <TableController.SearchForm
            value={(search_parameter || '') as string}
            onSubmit={(s) => {
              updateFilter({ search_parameter: s, page: null })
            }}
            placeholder="Search"
          />
          <TableController.Sorting
            options={sortingOptions}
            value={{
              sortId: _sortId,
              direction:
                _direction === '1' ? 'asc' : _direction === '-1' ? 'desc' : '',
            }}
            onChange={(val) => {
              updateFilter({
                _sortId: val.sortId,
                _direction:
                  val.direction === 'asc'
                    ? '1'
                    : val.direction === 'desc'
                      ? '-1'
                      : '',
              })
            }}
          />
        </TableController.Controller>
        <Divider />

        <CommentTable
          status={tab}
          loading={loadingList || loading}
          data={commentData.items}
          page={Number(page) || 1}
          pageCount={commentData.total_pages}
          onPageChange={(p) => updateFilter({ page: p })}
          rowsPerPage={rowsPerPage}
          fetchCommentList={fetchCommentList}
          setLoading={setLoading}
          selected={selected}
          setSelected={setSelected}
          updateFilter={updateFilter}
        />
      </Card>

      <StoreSyncingWarning />
    </SettingsLayout>
  )
}
